import { getDomainId } from 'api-client/helpers'
import { Tenant } from 'api-client/keystone.model'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { RootState } from 'app/store'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { listLeasePolicies } from 'openstack/components/vms/leases/actions'
import { pick, prop } from 'ramda'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { listDomains } from '../domains/actions'
import DomainFilter from '../domains/DomainFilter'
import CreateTenantModal from './CreateTenantModal'
import EditLeasePolicyModal from './EditLeasePolicyModal'
import EditTenantModal from './EditTenantModal'
import { ITenantDetailsPageTabs } from './model'
import { deleteTenant, listUserTenants } from './new-actions'
import TenantQuotasModal from './TenantQuotasModal'
import useTenantSelector from './useTenantSelector'

type ModelDataKey = DataKeys.AllTenants
type SelectorModel = Tenant

const isNotServiceTenant = (tenants) => !tenants.find((t) => t.name === 'service')

const usePrefParams = createUsePrefParamsHook<TablePrefsParams>('Tenants', listTablePrefs)

const searchTargets = ['name', 'domain']

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id }) =>
        routeManager.routes.userManagement.tenantDetails.path({
          id,
          tab: ITenantDetailsPageTabs.Overview,
        }),
    }),
  },
  { key: 'id', label: 'Tenant ID', display: false },
  { key: 'domain', label: 'Domain' },
  { key: 'description', label: 'Description' },
]

export default function TenantsListPage() {
  // States
  const [selectedDomain, setSelectedDomain] = useState<string>(getDomainId())
  const [selectedTenant, setSelectedTenant] = useState<SelectorModel>(null)
  const [showEditTenantModal, setShowEditTenantModal] = useState<boolean>(false)
  const [showTenantQuotasModal, setShowTenantQuotasModal] = useState<boolean>(false)
  const [showEditLeasePolicyModal, setShowEditLeasePolicyModal] = useState<boolean>(false)

  // Actions
  const { params, getParamsUpdater } = usePrefParams({})
  const { loading: domainsLoading } = useListAction(listDomains)
  const { reload: reloadLeasePolicies } = useListAction(listLeasePolicies)

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'

  // Fetch tenants based on user role
  const { data, loading, reload } = useTenantSelector(selectedDomain)

  const { reload: reloadUserTenants } = useListAction(listUserTenants)

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Edit',
      icon: 'edit',
      handleAction: ([tenant]) => {
        setSelectedTenant(tenant)
        setShowEditTenantModal(true)
      },
      refreshAfterSuccess: true,
    },
    {
      cond: ([tenant]) => {
        return isVmware ? !!tenant && tenant?.name !== 'service' : !!tenant
      },
      label: 'Manage Quotas',
      icon: 'chart-pie-simple',
      handleAction: ([tenant]) => {
        setSelectedTenant(tenant)
        setShowTenantQuotasModal(true)
      },
      disabledTooltip: (selected) =>
        isVmware && selected?.find((t) => t.name === 'service')
          ? 'Not allowed for service tenant'
          : '',
      refreshAfterSuccess: true,
    },
    {
      label: 'Manage Lease Policy',
      icon: 'key',
      handleAction: ([tenant]) => {
        setSelectedTenant(tenant)
        setShowEditLeasePolicyModal(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const onReload = useCallback(
    (refetch?: boolean, updateLoadingState?: boolean, updateRefreshingState?: boolean) => {
      reload(refetch, updateLoadingState, updateRefreshingState)
      reloadLeasePolicies(refetch, updateLoadingState, updateRefreshingState)
    },
    [reload, reloadLeasePolicies],
  )

  // Post delete action - reload user tenants if selected domain is the same as the deleted tenant's domain
  const postDeleteAction = useCallback(() => {
    if (getDomainId() === selectedDomain) {
      reloadUserTenants(true)
    }
  }, [selectedDomain, reloadUserTenants])

  return (
    <>
      <DocumentMeta title="Tenants" />
      <DomainFilter onChange={setSelectedDomain} value={selectedDomain} />
      <CreateTenantModal
        addRoute={routeManager.routes.userManagement.addTenant}
        reload={reload}
        domainId={selectedDomain}
      />
      {showEditTenantModal && (
        <EditTenantModal onClose={() => setShowEditTenantModal(false)} rows={[selectedTenant]} />
      )}
      {showTenantQuotasModal && (
        <TenantQuotasModal
          onClose={() => setShowTenantQuotasModal(false)}
          rows={[selectedTenant]}
        />
      )}
      {showEditLeasePolicyModal && (
        <EditLeasePolicyModal
          onClose={() => setShowEditLeasePolicyModal(false)}
          rows={[selectedTenant]}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.AllTenants}
        searchTargets={searchTargets}
        nameProp="name"
        uniqueIdentifier="id"
        loading={loading || domainsLoading}
        loadingMessage="Loading tenants..."
        onRefresh={onReload}
        data={data}
        columns={columns}
        addUrl={routeManager.routes.userManagement.addTenant.path()}
        addText="Create a New Tenant"
        getParamsUpdater={getParamsUpdater}
        // editUrl={(_, id) => routeManager.routes.userManagement.editTenant.path({ id })}
        // editCond={isNotServiceTenant}
        deleteCond={isNotServiceTenant}
        deleteAction={deleteTenant}
        postDeleteAction={postDeleteAction}
        batchActions={batchActions}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
